import React, { useEffect } from 'react'
import { getVentilatorSelector } from '../../selectors'
import { useDispatch, useSelector } from 'react-redux'
import { initialVentilatorState } from '../../store/ventilator.reducers'
import {
  saveVentilator,
  setVentilatorCheck,
  setMode,
  setAction,
  setBreathType,
  setSettingsRespRate,
  setTidalVolume,
  setInspPressure,
  setPEEP,
  setFiO2,
  setAirTemperature,
  setAirTemperatureUnit,
  setHumidityAir,
  setITime,
  setPressureSupport,
  setFlowRate,
  setSensitivity,
  setMeanAirwayPressure,
  setDeltaP,
  setHertz,
  setINO,
  setPeakInspPressure,
  setPlateauPressure,
  setAutoPEEP,
  setMinuteVent,
  setIERatio,
  setMeasurementRespRate,
  setExhaledTidalVolume,
  setETCuffPressureMeasurement,
  setInspiratoryResistance,
  setStaticCompliance,
  setExpiratoryTimeConstant,
} from '../../store/ventilator.actions'
import { IonCol, IonGrid, IonIcon, IonItem, IonItemDivider, IonLabel, IonList, IonRow, IonToggle } from '@ionic/react'
import { IOption } from '../../../../../common/common.types'
import { getVentilatorOptionsSelector } from '../../../../options/selectors/ventilatorOptions.selector'
import { useTranslation } from 'react-i18next'
import { IPHIComponent } from '../../../../../components/phi/phi.component'
import Toggle from '../../../../../components/toggle/Toggle.component'
import { copySharp } from 'ionicons/icons'
import SingleSelect from '../../../../../components/singleSelect/SingleSelect.component'
import NumberPad from '../../../../../components/numberPad/NumberPad.component'
import { getDefaultTempTypeSelector } from '../../../../configurations/selectors/defaultTempType.selector'
import { numberPadKeysWithDecimal, ratioKeyWithDecimal } from '../../../../../components/numberPad/NumberPad.types'
import './ventilator.content.css'

const VentilatorContent: React.FC<IPHIComponent> = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const ventilator = useSelector(getVentilatorSelector)
  const ventilatorOptions = useSelector(getVentilatorOptionsSelector)
  const { modeOptions, breathTypeOptions, actionOptions, previousVentilatorState } = ventilatorOptions
  const {
    action,
    airTemperatureUnit,
    ventilatorCheck,
    mode,
    breathType,
    settingsRespRate,
    tidalVolume,
    inspPressure,
    pEEP,
    fiO2,
    airTemperature,
    airTemperatureBool,
    humidityAir,
    iTime,
    pressureSupport,
    flowRate,
    sensitivity,
    meanAirwayPressure,
    deltaP,
    hertz,
    iNO,
    peakInspPressure,
    plateauPressure,
    autoPEEP,
    minuteVent,
    iERatio,
    measurementRespRate,
    exhaledTidalVolume,
    eTCuffPressureMeasurement,
    inspiratoryResistance,
    expiratoryTimeConstant,
    staticCompliance,
  } = ventilator

  const {
    settingsRespRate: prevSettingsResp,
    mode: prevMode,
    breathType: prevBreathType,
    tidalVolume: prevTidalVolume,
    inspPressure: prevInspPressure,
    pEEP: prevPEEP,
    fiO2: prevfiO2,
    airTemperatureUnit: prevAirTemperatureUnit,
    airTemperature: prevAirTemperature,
    airTemperatureBool: prevAirTemperatureBool,
    humidityAir: prevHumidityAir,
    iTime: prevITime,
    pressureSupport: prevPressureSupport,
    flowRate: prevFlowRate,
    sensitivity: prevSensitivity,
    meanAirwayPressure: prevMeanAirwayPressure,
    deltaP: prevDeltaP,
    hertz: prevHertz,
    iNO: previNO,
    peakInspPressure: prevPeakInspPressure,
    plateauPressure: prevPlateauPressure,
    autoPEEP: prevAutoPEEP,
    minuteVent: prevMinuteVent,
    iERatio: previERatio,
    measurementRespRate: prevMeasurementRespRate,
    exhaledTidalVolume: prevExhaledTidalVolume,
    eTCuffPressureMeasurement: prevETCuffPressureMeasurement,
    inspiratoryResistance: prevInspiratoryResistance,
    expiratoryTimeConstant: prevExpiratoryTimeConstant,
    staticCompliance: prevStaticCompliance,
  } = previousVentilatorState || initialVentilatorState
  const isChangeSettings = action?.id === 2

  const defaultTempType = useSelector(getDefaultTempTypeSelector)

  useEffect(() => {
    if (!airTemperatureUnit) {
      if (defaultTempType && defaultTempType === 'c') {
        dispatch(setAirTemperatureUnit(false))
      } else {
        dispatch(setAirTemperatureUnit(true))
      }
    }
  }, [])

  const save = () => {
    dispatch(saveVentilator())
  }

  const onCopy = () => {
    dispatch(setMode(prevMode))
    dispatch(setBreathType(prevBreathType))
    dispatch(setTidalVolume(prevTidalVolume))
    dispatch(setSettingsRespRate(prevSettingsResp))
    dispatch(setInspPressure(prevInspPressure))
    dispatch(setPEEP(prevPEEP))
    dispatch(setFiO2(prevfiO2))
    dispatch(setAirTemperature(prevAirTemperature))
    dispatch(setAirTemperatureUnit(prevAirTemperatureBool))
    dispatch(setHumidityAir(prevHumidityAir))
    dispatch(setITime(prevITime))
    dispatch(setFlowRate(prevFlowRate))
    dispatch(setPressureSupport(prevPressureSupport))
    dispatch(setSensitivity(prevSensitivity))
    dispatch(setMeanAirwayPressure(prevMeanAirwayPressure))
    dispatch(setDeltaP(prevDeltaP))
    dispatch(setHertz(prevHertz))
    dispatch(setINO(previNO))
    dispatch(setPeakInspPressure(prevPeakInspPressure))
    dispatch(setPlateauPressure(prevPlateauPressure))
    dispatch(setAutoPEEP(prevAutoPEEP))
    dispatch(setMinuteVent(prevMinuteVent))
    dispatch(setIERatio(previERatio))
    dispatch(setMeasurementRespRate(prevMeasurementRespRate))
    dispatch(setExhaledTidalVolume(prevExhaledTidalVolume))
    dispatch(setETCuffPressureMeasurement(prevETCuffPressureMeasurement))
    dispatch(setInspiratoryResistance(prevInspiratoryResistance))
    dispatch(setExpiratoryTimeConstant(prevExpiratoryTimeConstant))
    dispatch(setStaticCompliance(prevStaticCompliance))
    save()
  }

  const actionOnChange = (option: IOption) => {
    dispatch(setAction(option))
  }

  const modeOnChange = (option: IOption) => {
    dispatch(setMode(option))
    save()
  }

  const breathTypeOnChange = (option: IOption) => {
    dispatch(setBreathType(option))
    save()
  }

  const settingsRespRateOnChange = (values: string[]) => {
    dispatch(setSettingsRespRate(values.join('')))
    save()
  }

  const tidalVolumeOnChange = (values: string[]) => {
    dispatch(setTidalVolume(values.join('')))
    save()
  }

  const inspPressureOnChange = (values: string[]) => {
    dispatch(setInspPressure(values.join('')))
    save()
  }

  const pEEPOnChange = (values: string[]) => {
    dispatch(setPEEP(values.join('')))
    save()
  }

  const fiO2OnChange = (values: string[]) => {
    dispatch(setFiO2(values.join('')))
    save()
  }

  const airTemperatureOnChange = (values: string[]) => {
    dispatch(setAirTemperature(values.join('')))
    save()
  }

  const humidityAirOnChange = (values: string[]) => {
    dispatch(setHumidityAir(values.join('')))
    save()
  }

  const iTimeOnChange = (values: string[]) => {
    dispatch(setITime(values.join('')))
    save()
  }

  const pressureSupportOnChange = (values: string[]) => {
    dispatch(setPressureSupport(values.join('')))
    save()
  }

  const flowRateOnChange = (values: string[]) => {
    dispatch(setFlowRate(values.join('')))
    save()
  }

  const sensitivityOnChange = (values: string[]) => {
    dispatch(setSensitivity(values.join('')))
    save()
  }

  const meanAirwayPressureOnChange = (values: string[]) => {
    dispatch(setMeanAirwayPressure(values.join('')))
    save()
  }

  const deltaPOnChange = (values: string[]) => {
    dispatch(setDeltaP(values.join('')))
    save()
  }

  const hertzOnChange = (values: string[]) => {
    dispatch(setHertz(values.join('')))
    save()
  }

  const iNOOnChange = (values: string[]) => {
    dispatch(setINO(values.join('')))
    save()
  }

  const peakInspPressureOnChange = (values: string[]) => {
    dispatch(setPeakInspPressure(values.join('')))
    save()
  }

  const plateauPressureOnChange = (values: string[]) => {
    dispatch(setPlateauPressure(values.join('')))
    save()
  }

  const autoPEEPOnChange = (values: string[]) => {
    dispatch(setAutoPEEP(values.join('')))
    save()
  }

  const minuteVentOnChange = (values: string[]) => {
    dispatch(setMinuteVent(values.join('')))
    save()
  }

  const iERatioOnChange = (values: string[]) => {
    dispatch(setIERatio(values.join('')))
    save()
  }

  const measurementRespRateOnChange = (values: string[]) => {
    dispatch(setMeasurementRespRate(values.join('')))
    save()
  }

  const exhaledTidalVolumeOnChange = (values: string[]) => {
    dispatch(setExhaledTidalVolume(values.join('')))
    save()
  }

  const eTCuffPressureMeasurementOnChange = (values: string[]) => {
    dispatch(setETCuffPressureMeasurement(values.join('')))
    save()
  }

  const inspiratoryResistanceOnChange = (values: string[]) => {
    dispatch(setInspiratoryResistance(values.join('')))
    save()
  }

  const staticComplianceOnChange = (values: string[]) => {
    dispatch(setStaticCompliance(values.join('')))
    save()
  }

  const expiratoryTimeConstantOnChange = (values: string[]) => {
    dispatch(setExpiratoryTimeConstant(values.join('')))
    save()
  }

  const TemperatureToggle = (
    <IonItem lines='none'>
      <IonLabel
        color={!airTemperatureBool ? 'primary-alt' : 'medium'}
        className={!airTemperatureBool ? 'airTemp-toggle toggle-prefix-width' : 'toggle-prefix-width'}>
        {t('ventilator.C')}
      </IonLabel>
      <IonToggle
        data-testid='AirTemperatureUnit'
        checked={airTemperatureBool}
        onIonChange={(e) => {
          dispatch(setAirTemperatureUnit(e.detail.checked))
        }}
        color='zoll-blue-secondary'
        className='temperature-ion-toggle'
      />
      <IonLabel
        color={airTemperatureBool ? 'primary-alt' : 'medium'}
        className={airTemperatureBool ? 'airTemp-toggle toggle-postfix-padding' : 'toggle-postfix-padding'}>
        {t('ventilator.F')}
      </IonLabel>
    </IonItem>
  )

  const FixedInput = (input: string, unit: string | undefined) => {
    if (isChangeSettings) {
      return (
        <IonCol>
          <div className='fixed-input'>
            <IonLabel className='input-label'>{t('ventilator.previous')}</IonLabel>
            <IonLabel>{input ? `${input} ${unit || ''}` : 'N/A'}</IonLabel>
          </div>
        </IonCol>
      )
    }
  }

  const ChangeSettingsLabels = (label: string, showToggle?: boolean) => {
    if (isChangeSettings) {
      return (
        <IonRow>
          <IonCol>
            <IonLabel className='header-text'>{label}</IonLabel>
          </IonCol>
          {showToggle && <IonCol>{TemperatureToggle}</IonCol>}
        </IonRow>
      )
    }
  }

  return (
    <IonList id='VentilatorContent'>
      <IonGrid className='ion-no-padding'>
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              label={t('ventilator.settingAction')}
              dataTestID='Action'
              options={actionOptions}
              action={actionOnChange}
              listHeader={t('ventilator.settingAction')}
              selectedOption={action}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        <IonRow>
          <IonCol>
            <Toggle
              className='toggle-item'
              rightLabel={t('ventilator.ventilatorCheck')}
              dataTestId='VentilatorCheck'
              isChecked={ventilatorCheck}
              onChangeAction={setVentilatorCheck}
            />
          </IonCol>
          {isChangeSettings && (
            <IonCol>
              <div onClick={onCopy} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <IonIcon size='large' color='primary-alt' icon={copySharp} />
                <IonLabel style={{ flex: 'none', marginLeft: '4px' }} color='primary-alt'>
                  Copy Previous
                </IonLabel>
              </div>
            </IonCol>
          )}
        </IonRow>

        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              label={t('ventilator.mode')}
              dataTestID='Mode'
              options={modeOptions}
              action={modeOnChange}
              listHeader='Mode'
              selectedOption={mode}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              label={t('ventilator.breathType')}
              dataTestID='BreathType'
              options={breathTypeOptions}
              action={breathTypeOnChange}
              listHeader='Breath Type'
              selectedOption={breathType}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.respRate'))}
        <IonRow className='mb-1'>
          {FixedInput(prevSettingsResp, 'bpm')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='RespRate'
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.respRate')}
              dataTestId='RespRate'
              values={settingsRespRate?.split('')}
              onNumPadClick={settingsRespRateOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.tidalVolume'))}
        <IonRow className='mb-1'>
          {FixedInput(prevTidalVolume, 'mL')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='TidalVolume'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.tidalVolume')}
              dataTestId='TidalVolume'
              values={tidalVolume?.split('')}
              onNumPadClick={tidalVolumeOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.inspPressure'))}
        <IonRow className='mb-1'>
          {FixedInput(prevInspPressure, 'cmH2O')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='InspPressure'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.inspPressure')}
              dataTestId='InspPressure'
              values={inspPressure?.split('')}
              onNumPadClick={inspPressureOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.pEEP'))}
        <IonRow className='mb-1'>
          {FixedInput(prevPEEP, 'cmH2O')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='PEEP'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.pEEP')}
              dataTestId='PEEP'
              values={pEEP?.split('')}
              onNumPadClick={pEEPOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.fiO2'))}
        <IonRow className='mb-1'>
          {FixedInput(prevfiO2, '')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='FiO2'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.fiO2')}
              dataTestId='FiO2'
              values={fiO2?.split('')}
              onNumPadClick={fiO2OnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.airTemperature'), true)}
        <IonRow className='mb-1'>
          {FixedInput(prevAirTemperature, prevAirTemperatureUnit?.name)}
          <IonCol className='picklist'>
            <NumberPad
              elementId='AirTemperature'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.airTemperature')}
              dataTestId='AirTemperature'
              values={airTemperature?.split('')}
              onNumPadClick={airTemperatureOnChange}
            />
          </IonCol>
          {!isChangeSettings && <IonCol>{TemperatureToggle}</IonCol>}
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.humidityAir'))}
        <IonRow className='mb-1'>
          {FixedInput(prevHumidityAir, '%')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='HumidityAir'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.humidityAir')}
              dataTestId='HumidityAir'
              values={humidityAir?.split('')}
              onNumPadClick={humidityAirOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.iTime'))}
        <IonRow className='mb-1'>
          {FixedInput(prevITime, 'seconds')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='ITime'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.iTime')}
              dataTestId='ITime'
              values={iTime?.split('')}
              onNumPadClick={iTimeOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.pressureSupport'))}
        <IonRow className='mb-1'>
          {FixedInput(prevPressureSupport, 'seconds')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='PressureSupport'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.pressureSupport')}
              dataTestId='PressureSupport'
              values={pressureSupport?.split('')}
              onNumPadClick={pressureSupportOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.flowRate'))}
        <IonRow className='mb-1'>
          {FixedInput(prevFlowRate, 'Lpm')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='FlowRate'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.flowRate')}
              dataTestId='FlowRate'
              values={flowRate?.split('')}
              onNumPadClick={flowRateOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.sensitivity'))}
        <IonRow className='mb-1'>
          {FixedInput(prevSensitivity, '')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='Sensitivity'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.sensitivity')}
              dataTestId='Sensitivity'
              values={sensitivity?.split('')}
              onNumPadClick={sensitivityOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.meanAirwayPressure'))}
        <IonRow className='mb-1'>
          {FixedInput(prevMeanAirwayPressure, 'cmH2O')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='MeanAirwayPressure'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.meanAirwayPressure')}
              dataTestId='MeanAirwayPressure'
              values={meanAirwayPressure?.split('')}
              onNumPadClick={meanAirwayPressureOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.deltaP'))}
        <IonRow className='mb-1'>
          {FixedInput(prevDeltaP, 'cmH2O')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='DeltaP'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.deltaP')}
              dataTestId='DeltaP'
              values={deltaP?.split('')}
              onNumPadClick={deltaPOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.hertz'))}
        <IonRow className='mb-1'>
          {FixedInput(prevHertz, '')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='Hertz'
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.hertz')}
              dataTestId='Hertz'
              values={hertz?.split('')}
              validCharacters={numberPadKeysWithDecimal}
              onNumPadClick={hertzOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.iNO'))}
        <IonRow className='mb-1'>
          {FixedInput(previNO, '')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='INO'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.iNO')}
              dataTestId='INO'
              values={iNO?.split('')}
              onNumPadClick={iNOOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.peakInspPressure'))}
        <IonRow className='mb-1'>
          {FixedInput(prevPeakInspPressure, 'cmH2O')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='PeakInspPressure'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.peakInspPressure')}
              dataTestId='PeakInspPressure'
              values={peakInspPressure?.split('')}
              onNumPadClick={peakInspPressureOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.plateauPressure'))}
        <IonRow className='mb-1'>
          {FixedInput(prevPlateauPressure, 'cmH2O')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='PlateauPressure'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.plateauPressure')}
              dataTestId='PlateauPressure'
              values={plateauPressure?.split('')}
              onNumPadClick={plateauPressureOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.autoPEEP'))}
        <IonRow className='mb-1'>
          {FixedInput(prevAutoPEEP, 'cmH2O')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='AutoPeep'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.autoPEEP')}
              dataTestId='AutoPeep'
              values={autoPEEP?.split('')}
              onNumPadClick={autoPEEPOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.minuteVent'))}
        <IonRow className='mb-1'>
          {FixedInput(prevMinuteVent, 'Lpm')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='MinuteVent'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.minuteVent')}
              dataTestId='MinuteVent'
              values={minuteVent?.split('')}
              onNumPadClick={minuteVentOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.iERatio'))}
        <IonRow className='mb-1'>
          {FixedInput(previERatio, '')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='IERatio'
              validCharacters={ratioKeyWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.iERatio')}
              dataTestId='IERatio'
              values={iERatio?.split('')}
              onNumPadClick={iERatioOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.respRate'))}
        <IonRow className='mb-1'>
          {FixedInput(prevMeasurementRespRate, 'bpm')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='MeasurementRespRate'
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.respRate')}
              dataTestId='MeasurementRespRate'
              values={measurementRespRate?.split('')}
              onNumPadClick={measurementRespRateOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.exhaledTidalVolume'))}
        <IonRow className='mb-1'>
          {FixedInput(prevExhaledTidalVolume, '')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='ExhaledTidalVolume'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.exhaledTidalVolume')}
              dataTestId='ExhaledTidalVolume'
              values={exhaledTidalVolume?.split('')}
              onNumPadClick={exhaledTidalVolumeOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.eTCuffPressureMeasurement'))}
        <IonRow className='mb-1'>
          {FixedInput(prevETCuffPressureMeasurement, '(cmH2O)')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='ETCuffPressureMeasurement'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.eTCuffPressureMeasurement')}
              dataTestId='ETCuffPressureMeasurement'
              values={eTCuffPressureMeasurement?.split('')}
              onNumPadClick={eTCuffPressureMeasurementOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.inspiratoryResistance'))}
        <IonRow className='mb-1'>
          {FixedInput(prevInspiratoryResistance, 'cmH2O(l/s)')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='InspiratoryResistance'
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.inspiratoryResistance')}
              dataTestId='InspiratoryResistance'
              values={inspiratoryResistance?.split('')}
              onNumPadClick={inspiratoryResistanceOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.staticCompliance'))}
        <IonRow className='mb-1'>
          {FixedInput(prevStaticCompliance, '(ml/cmH2O)')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='StaticCompliance'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.staticCompliance')}
              dataTestId='StaticCompliance'
              values={staticCompliance?.split('')}
              onNumPadClick={staticComplianceOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        {ChangeSettingsLabels(t('ventilator.expiratoryTimeConstant'))}
        <IonRow className='mb-1'>
          {FixedInput(prevExpiratoryTimeConstant, 'seconds')}
          <IonCol className='picklist'>
            <NumberPad
              elementId='ExpiratoryTimeConstant'
              validCharacters={numberPadKeysWithDecimal}
              label={isChangeSettings ? t('ventilator.changedTo') : t('ventilator.expiratoryTimeConstant')}
              dataTestId='ExpiratoryTimeConstant'
              values={expiratoryTimeConstant?.split('')}
              onNumPadClick={expiratoryTimeConstantOnChange}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
      </IonGrid>
      <IonItemDivider color='light' />
    </IonList>
  )
}

export default VentilatorContent
